import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import { useReadOnly } from '../hooks/useReadOnly';

/**
 * Start days and route days for every open bid (owner, Oct 6 2026: "give me
 * start days and route days for all new bids"). What is on each bid now, and
 * for a mowing bid what accepting it would book (the same dates the customer's
 * page shows). "Use this" saves the suggestion on the bid. Loads only when
 * opened — working out a day for each bid takes a moment.
 */
const fmt = (d?: string | null) => d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '';

export default function BidStartDays({ onOpenBid }: { onOpenBid?: (id: number) => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const readOnly = useReadOnly();
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery('bid-start-days', () => api.get('/quotes/start-days').then(r => r.data), { enabled: open, staleTime: 60_000 });
  const bids: any[] = data?.bids || [];
  const missing = bids.filter(b => !b.start_date || (b.mowing && b.service_day == null)).length;

  async function use(b: any) {
    if (!b.suggest) return;
    setBusy(b.id);
    try {
      const body: any = {};
      if (!b.start_date && b.suggest.start_date) body.start_date = b.suggest.start_date;
      if (b.service_day == null && b.suggest.service_day != null) { body.service_day = b.suggest.service_day; if (b.suggest.route_id) body.route_id = b.suggest.route_id; }
      if (!Object.keys(body).length) { toast.info('Nothing missing on that bid'); return; }
      await api.put(`/quotes/${b.id}`, body);
      toast.success(`Saved on bid #${b.id}`);
      refetch(); qc.invalidateQueries('quotes');
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Could not save that'); }
    finally { setBusy(null); }
  }

  return (
    <div className="card p-3">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between text-left">
        <span className="font-semibold text-gray-900">Start days &amp; route days</span>
        <span className="text-xs text-gray-500">{open ? (isLoading ? 'Working out days…' : `${bids.length} open bid${bids.length === 1 ? '' : 's'}${missing ? ` · ${missing} missing a day` : ''}`) : 'Show'}</span>
      </button>
      {open && isError && <p className="text-sm text-red-700 mt-2">Could not load them. <button className="underline" onClick={() => refetch()}>Try again</button></p>}
      {open && !isLoading && !isError && (
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500">
              <th className="py-1 pr-3">Bid</th><th className="py-1 pr-3">Start</th><th className="py-1 pr-3">Route day</th><th className="py-1" />
            </tr></thead>
            <tbody>
              {bids.map(b => {
                const needsStart = !b.start_date;
                const needsDay = b.mowing && b.service_day == null;
                const canUse = !readOnly && b.suggest && ((needsStart && b.suggest.start_date) || (needsDay && b.suggest.service_day != null));
                return (
                  <tr key={b.id} className="border-t border-gray-100 align-top">
                    <td className="py-1.5 pr-3">
                      <button type="button" onClick={() => onOpenBid?.(b.id)} className="font-medium text-gray-900 hover:underline text-left">{b.customer}</button>
                      <div className="text-xs text-gray-500">#{b.id} · {b.mowing ? (b.frequency || 'mowing').replace('_', ' ') : 'one-time work'} · {b.status}{b.address ? ` · ${b.address}` : ''}</div>
                    </td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">
                      {b.start_date ? fmt(b.start_date) : <span className="text-amber-700">none{b.suggest?.start_date ? ` → ${fmt(b.suggest.start_date)}` : ''}</span>}
                    </td>
                    <td className="py-1.5 pr-3">
                      {!b.mowing ? <span className="text-gray-400">—</span>
                        : b.service_day != null ? <>{b.day_name}{b.route_name ? <span className="text-xs text-gray-500 block">{b.route_name}</span> : null}</>
                        : <span className="text-amber-700">none{b.suggest?.day_name ? ` → ${b.suggest.day_name}` : ''}{b.suggest?.route_name ? <span className="text-xs block">{b.suggest.route_name}</span> : null}</span>}
                      {b.note && <span className="text-xs text-gray-500 block">{b.note}</span>}
                    </td>
                    <td className="py-1.5 text-right">
                      {canUse && <button type="button" disabled={busy === b.id} onClick={() => use(b)} className="text-xs font-semibold text-brand-700 underline disabled:opacity-50">Use this</button>}
                      {!b.mowing && needsStart && <span className="text-xs text-gray-500">set it on the bid</span>}
                    </td>
                  </tr>
                );
              })}
              {!bids.length && <tr><td colSpan={4} className="py-3 text-gray-500">No open bids.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
